import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';

export interface TaskStep {
  title: string;
  completed: boolean;
}

export interface TaskDetail {
  id: string;
  title: string;
  subtitle: string;
  summary: string;
  currentAction: string;
  status: string;
  agentId: string;
  steps: TaskStep[];
  /** Full VFS paths of the files the task produced (`display.artifacts`, e.g.
   *  `workspaces/<slug>/artifacts/<name>`). The detail's Files section resolves + opens each. */
  artifacts: string[];
  workspaceId: string;
  /** The Maestro chat this task was born in — what "Resolve with Maestro" continues, so the agent already
   *  has the task's history. Empty when the origin conversation is not a chat the user can be dropped into
   *  (a routine's PULSE thread, a goal tool's own conversation) — the CTA seeds a new chat then. */
  resolveConversationId?: string;
  date: string | null;
  /** When the task was created — shown on the detail so you can see how long it's been running. */
  createdDate: string | null;
  /** This task tracks a dashboard BUILD, which runs service-side with no agent conversation behind it
   *  (see the harness's `dashboard-build-task.ts`). The activity section reads it to say so, rather than
   *  promising a transcript that will never arrive. */
  isDashboardBuild?: boolean;
}

export type TaskDetailState = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; task: TaskDetail };

// Fetches one task's normalized detail (/api/task/:id). Shared by the task
// detail page and the Symphony task-details side panel. Cached via react-query
// so re-opening a task the user just viewed renders instantly (no loading
// shimmer) and refetches in the background once stale.
export function useTaskDetail(id: string): TaskDetailState {
  const { data, isError } = useQuery({
    queryKey: ['task-detail', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await apiFetch(`${API_BASE}/api/task/${encodeURIComponent(id)}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as { task?: TaskDetail };
      if (!body.task) throw new Error('no task');
      return body.task;
    },
  });

  if (data) return { kind: 'ready', task: data };
  if (isError) return { kind: 'error' };
  return { kind: 'loading' };
}
